import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense } from "react";
import { createTheme, getContrastRatio, makeStyles, ThemeProvider } from "@material-ui/core/styles";
import { color, bgcolor } from "@material-ui/system";
import clsx from "clsx";
import Card from "@material-ui/core/Card";
import CardMedia from "@material-ui/core/CardMedia";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import CardHeader from "@material-ui/core/CardHeader";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import WarningIcon from "@material-ui/icons/Warning";
import Icon from "@material-ui/core/Icon";
import { ControlType } from "./property-controls";
import { FormattedMessage } from "react-intl";
export const contentBoxPropertyControls = {
    type: ControlType.Object,
    title: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.title", defaultMessage: "Common settings" })),
    propertyControls: {
        header: {
            type: ControlType.String,
            title: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.header.label", defaultMessage: "Title" }))
        },
        iconClass: {
            type: ControlType.String,
            title: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.icon-class.label", defaultMessage: "Icon" })),
            description: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.icon-class.description", defaultMessage: "Enter the name of an icon from Font Awesome" }))
        },
        backgroundColor: {
            type: ControlType.Color,
            title: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.backgroundColor.label", defaultMessage: "Background color" })),
            description: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.backgroundColor.description", defaultMessage: "Background color for the box" })),
            coerce: (_, boxProps) => {
                if (boxProps.grey)
                    return "grey.200";
            }
        },
        headerColor: {
            type: ControlType.Color,
            title: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.headerColor.label", defaultMessage: "Title color" })),
            description: (_jsx(FormattedMessage, { id: "dploy.grid.content-box.headerColor.description", defaultMessage: "Text color of the title and icon" })),
            coerce: (_, boxProps) => {
                if (boxProps.grey)
                    return "grey.500";
            }
        }
    }
};
function ContentBox(props) {
    const { iconClass, avatar = iconClass && (_jsx(Icon, { fontSize: "inherit", className: `fa fa-${iconClass}` })), loading, error, backgroundColor, children, fallbackContent, headerAction, ...rest } = props;
    let action = headerAction || _jsx("div", {});
    if (loading)
        action = _jsx(CircularProgress, { size: 24 });
    else if (error && !headerAction)
        action = (_jsx(Tooltip, { title: typeof error === "string" ? error : error.message, children: _jsx(WarningIcon, { color: "error" }) }));
    const themeOverride = (theme) => {
        const paper = bgcolor({
            bgcolor: backgroundColor,
            theme
        })?.backgroundColor;
        if (!paper)
            return theme;
        const switchDarkMode = getContrastRatio(paper, theme.palette.text.primary) < 3;
        if (switchDarkMode) {
            const { text, action, divider, type, ...palette } = theme.palette;
            return createTheme({
                ...theme,
                palette: {
                    ...palette,
                    type: type === "dark" ? "light" : "dark",
                    background: {
                        paper
                    }
                }
            });
        }
        return {
            ...theme,
            palette: {
                ...theme.palette,
                background: {
                    ...theme.palette.background,
                    paper
                }
            }
        };
    };
    return (_jsx(ThemeProvider, { theme: themeOverride, children: _jsx(Suspense, { fallback: _jsx(ContentCard, { avatar: avatar, headerAction: _jsx(CircularProgress, { size: 24 }), ...rest, children: fallbackContent }), children: _jsx(ContentCard, { avatar: avatar, headerAction: action, ...rest, children: children }) }) }));
}
ContentBox.displayName = "ContentBox";
export default ContentBox;
const useStyles = makeStyles(theme => ({
    root: {
        "&:empty": {
            display: "none"
        },
        height: "100%",
        display: "flex",
        flexDirection: "column"
    },
    headerAction: {
        width: 48,
        height: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    headerAvatar: {
        fontSize: 24,
        color: (props) => color({ color: props.headerColor, theme })?.color
    },
    headerTitle: {
        color: (props) => color({ color: props.headerColor, theme })?.color
    },
    headerSubheader: {
        color: (props) => color({ color: props.headerColor, theme })?.color
    },
    header: {},
    content: {
        paddingTop: theme.spacing(1),
        flexGrow: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        "& > *": {
            flexGrow: 1
        },
        position: "relative"
    },
    paddingGradient: {
        position: "absolute",
        bottom: 0,
        left: theme.spacing(1),
        right: theme.spacing(1),
        [theme.breakpoints.up("sm")]: {
            left: theme.spacing(2),
            right: theme.spacing(2)
        },
        height: theme.spacing(4),
        background: (props) => `linear-gradient(
			  to bottom,
			  rgba(0,0,0,0),
			  ${theme.palette.background.paper} 100%
			)`,
        zIndex: 1,
        pointerEvents: "none"
    },
    actions: {
        justifyContent: "center"
    }
}), { name: "DashboardContentCard" });
function ContentCard(props) {
    const { className, header, subheader, avatar, children, media, actions, headerAction, scrollGradient } = props;
    const classes = useStyles(props);
    return (_jsxs(Card, { className: clsx(className, classes.root), elevation: 1, children: [_jsx(CardHeader, { className: classes.header, classes: {
                    action: classes.headerAction,
                    avatar: classes.headerAvatar,
                    title: classes.headerTitle,
                    subheader: classes.headerSubheader
                }, avatar: avatar, title: header, subheader: subheader, action: headerAction }), media && _jsx(CardMedia, { children: media }), _jsxs(CardContent, { className: classes.content, children: [children, scrollGradient && _jsx("div", { className: classes.paddingGradient })] }), actions && (_jsx(CardActions, { className: classes.actions, children: actions }))] }));
}
//# sourceMappingURL=ContentBox.js.map